/** Formules sphériques du serveur (generex_engine.geometry, terrain, mapdraw), pour les aperçus pendant un
 *  glissement. Points en [lon, lat] (ordre GeoJSON). Le serveur recalcule et fait foi au relâcher. */

export type LonLat = [number, number];

const RAD = Math.PI / 180;
const R_KM = 6371;
const R_MOVE_M = 6_378_137;

export function bearingDeg(from: LonLat, to: LonLat): number {
  const lat1 = from[1] * RAD;
  const lat2 = to[1] * RAD;
  const dLon = (to[0] - from[0]) * RAD;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  const deg = (Math.atan2(y, x) * 180) / Math.PI;
  return deg < 0 ? deg + 360 : deg;
}

export function distanceM(a: LonLat, b: LonLat): number {
  const dLat = (b[1] - a[1]) * RAD;
  const dLon = (b[0] - a[0]) * RAD;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * RAD) * Math.cos(b[1] * RAD) * Math.sin(dLon / 2) ** 2;
  return R_KM * 1000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function destination(origin: LonLat, bearing: number, angular: number): LonLat {
  const brg = bearing * RAD;
  const lat1 = origin[1] * RAD;
  const lon1 = origin[0] * RAD;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(brg));
  const lon2 =
    lon1 +
    Math.atan2(Math.sin(brg) * Math.sin(angular) * Math.cos(lat1), Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2));
  return [lon2 / RAD, lat2 / RAD];
}

/** `moveCoordinate` (rayon de 6 371 km). */
export function moveKm(origin: LonLat, bearing: number, km: number): LonLat {
  return destination(origin, bearing, km / R_KM);
}

/** `move(from:angle:distance:)` (rayon de 6 378 137 m). */
export function moveM(origin: LonLat, bearing: number, meters: number): LonLat {
  return destination(origin, bearing, meters / R_MOVE_M);
}

/** Abscisse (m) d'un point le long d'un axe partant de `start` au cap `axis`. */
export function alongAxis(start: LonLat, axis: number, point: LonLat): number {
  return distanceM(start, point) * Math.cos((bearingDeg(start, point) - axis) * RAD);
}

/* --- CENTAC : barre d'une LIMA accrochée aux deux limites du camp (generex_engine.centac.rail_cross) --- */

const R_HELPER_M = 6_378_137; // CoordinateCalculationHelper de l'iPad
const M_PER_DEG = 111_320;

export function helperDistanceM(a: LonLat, b: LonLat): number {
  const dLat = (b[1] - a[1]) * RAD;
  const dLon = (b[0] - a[0]) * RAD;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * RAD) * Math.cos(b[1] * RAD) * Math.sin(dLon / 2) ** 2;
  return R_HELPER_M * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function projectOnSegment(p: LonLat, a: LonLat, b: LonLat): LonLat {
  const mLon = M_PER_DEG * Math.cos(a[1] * RAD);
  const px = (p[0] - a[0]) * mLon;
  const py = (p[1] - a[1]) * M_PER_DEG;
  const bx = (b[0] - a[0]) * mLon;
  const by = (b[1] - a[1]) * M_PER_DEG;
  const len2 = bx * bx + by * by;
  if (!(len2 > 0)) return a;
  const t = Math.max(0, Math.min(1, (px * bx + py * by) / len2));
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

export function closestPoint(polyline: LonLat[], target: LonLat): LonLat | null {
  let best: LonLat | null = polyline[0] ?? null;
  let bestD = Infinity;
  for (let i = 0; i + 1 < polyline.length; i++) {
    const p = projectOnSegment(target, polyline[i]!, polyline[i + 1]!);
    const d = helperDistanceM(target, p);
    if (d < bestD) [best, bestD] = [p, d];
  }
  return best;
}

/** Abscisse curviligne (m) du point de la polyligne le plus proche de la cible (arc_length_to_closest_point du
 *  serveur) : repère des glissements de LIMA hors CENTAC. */
export function arcPosition(polyline: LonLat[], target: LonLat): number {
  let accumulated = 0;
  let bestD = Infinity;
  let bestArc = 0;
  for (let i = 0; i + 1 < polyline.length; i++) {
    const a = polyline[i]!;
    const b = polyline[i + 1]!;
    const p = projectOnSegment(target, a, b);
    const d = helperDistanceM(target, p);
    if (d < bestD) [bestD, bestArc] = [d, accumulated + helperDistanceM(a, p)];
    accumulated += helperDistanceM(a, b);
  }
  return bestArc;
}

/** Intersection du segment [a, b] avec la polyligne, la plus proche de a (plan local centré sur a). */
function segmentPolylineIntersection(a: LonLat, b: LonLat, polyline: LonLat[]): LonLat | null {
  const mLon = M_PER_DEG * Math.cos(a[1] * RAD);
  const proj = (c: LonLat): [number, number] => [(c[0] - a[0]) * mLon, (c[1] - a[1]) * M_PER_DEG];
  const [rx, ry] = proj(b);
  let bestT = Infinity;
  let best: LonLat | null = null;
  for (let i = 0; i + 1 < polyline.length; i++) {
    const [cx, cy] = proj(polyline[i]!);
    const [dx, dy] = proj(polyline[i + 1]!);
    const [sx, sy] = [dx - cx, dy - cy];
    const denom = rx * sy - ry * sx;
    if (Math.abs(denom) < 1e-6) continue;
    const t = (cx * sy - cy * sx) / denom;
    const u = (cx * ry - cy * rx) / denom;
    if (t < 0 || t > 1 || u < 0 || u > 1 || t >= bestT) continue;
    bestT = t;
    best = [a[0] + (t * rx) / mLon, a[1] + (t * ry) / M_PER_DEG];
  }
  return best;
}

/** Barre d'une LIMA accrochée aux deux limites : perpendiculaire à l'axe des rails par `center`, coupée par
 *  chaque limite (à défaut, point le plus proche) et prolongée de `margin` m. Renvoie [côté droit, milieu,
 *  côté gauche] comme le serveur ; null si les limites se touchent. */
export function railCross(
  left: LonLat[],
  right: LonLat[],
  center: LonLat,
  margin = 500,
): [LonLat, LonLat, LonLat] | null {
  const first = left[0];
  const last = left[left.length - 1];
  if (!first || !last || right.length < 2) return null;
  const axis = bearingDeg(first, last);
  const plus = moveM(center, axis + 90, 20_000);
  const minus = moveM(center, axis - 90, 20_000);
  const lh = segmentPolylineIntersection(minus, plus, left) ?? closestPoint(left, center);
  const rh = segmentPolylineIntersection(minus, plus, right) ?? closestPoint(right, center);
  if (!lh || !rh) return null;
  const span = helperDistanceM(lh, rh);
  if (!(span > 1)) return null;
  const mid: LonLat = [(lh[0] + rh[0]) / 2, (lh[1] + rh[1]) / 2];
  const cross = bearingDeg(lh, rh);
  const half = span / 2 + margin;
  return [moveM(mid, cross, half), mid, moveM(mid, cross + 180, half)];
}

/* --- Glissement d'une LIMA (generex_engine.mapdraw) --------------------------------------------------------- */

/** Point à l'abscisse curviligne donnée le long de la polyligne (dernier point au-delà). */
export function pointAlongPolyline(polyline: LonLat[], distance: number): LonLat | null {
  if (polyline.length < 2 || distance < 0) return null;
  let accumulated = 0;
  for (let i = 0; i + 1 < polyline.length; i++) {
    const a = polyline[i]!;
    const b = polyline[i + 1]!;
    const length = helperDistanceM(a, b);
    if (accumulated + length >= distance) return moveM(a, bearingDeg(a, b), distance - accumulated);
    accumulated += length;
  }
  return polyline[polyline.length - 1] ?? null;
}

/** limaCrossing : du point de la limite de référence le plus proche de `reference` au point de l'autre limite
 *  le plus proche de lui, prolongé de `margin` m de chaque côté. */
export function limaCrossing(
  reference: LonLat,
  left: LonLat[],
  right: LonLat[],
  margin = 500,
): [LonLat, LonLat, LonLat] | null {
  const leftHit = closestPoint(left, reference);
  const rightHit = leftHit ? closestPoint(right, leftHit) : null;
  if (!leftHit || !rightHit) return null;
  const base = helperDistanceM(leftHit, rightHit);
  if (!(base > 1)) return null;
  const center: LonLat = [(leftHit[0] + rightHit[0]) / 2, (leftHit[1] + rightHit[1]) / 2];
  const cross = bearingDeg(leftHit, rightHit);
  const half = base / 2 + margin;
  return [moveM(center, cross, half), center, moveM(center, cross + 180, half)];
}

/** Abscisse d'un point le long d'un repère (origine, cap) : projection sur l'axe, comme le serveur. */
export function trackPosition(origin: LonLat, axis: number, point: LonLat): number {
  return helperDistanceM(origin, point) * Math.cos(((bearingDeg(origin, point) - axis) * Math.PI) / 180);
}

/** Tracé orienté pour finir à l'extrémité portant l'étiquette : la plus proche de la limite `other`. */
export function labelLast(coords: LonLat[], other: LonLat[]): LonLat[] {
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (!first || !last) return coords;
  const gap = (p: LonLat) => {
    const hit = closestPoint(other, p);
    return hit ? helperDistanceM(p, hit) : Infinity;
  };
  return gap(first) <= gap(last) ? [...coords].reverse() : coords;
}

/* --- Dépassement des LIMA à l'écran (affichage seulement : données, exports et OPO inchangés) --------------- */

/** Distance (m) d'un point à la plus proche des polylignes (Infinity sans polyligne exploitable). */
export function gapToPolylines(point: LonLat, polylines: readonly LonLat[][]): number {
  let best = Infinity;
  for (const polyline of polylines) {
    if (polyline.length < 2) continue;
    const hit = closestPoint(polyline, point);
    if (hit) best = Math.min(best, helperDistanceM(point, hit));
  }
  return best;
}

/** Prolongement (m) d'une extrémité qui dépasse déjà la limite de `existingM` dans les données, pour que le
 *  dépassement atteigne `minPx` pixels à `metersPerPx` m/px : max(0, minPx × m/px − existingM). Jamais négatif
 *  (le tracé n'est jamais raccourci) ; nul si le dépassement est inconnu. */
export function overshootExtensionM(existingM: number, minPx: number, metersPerPx: number): number {
  if (!Number.isFinite(existingM) || !(metersPerPx > 0) || !(minPx > 0)) return 0;
  return Math.max(0, minPx * metersPerPx - existingM);
}

const samePoint = (a: LonLat, b: LonLat) => Math.abs(a[0] - b[0]) < 1e-12 && Math.abs(a[1] - b[1]) < 1e-12;

/** Cap (°) du segment terminal, de l'intérieur du tracé vers l'extrémité (`atEnd` : la dernière, sinon la
 *  première), pris au premier sommet distinct de l'extrémité ; null si le tracé n'a pas deux points distincts. */
export function endBearing(coords: readonly LonLat[], atEnd: boolean): number | null {
  const n = coords.length;
  if (n < 2) return null;
  const tip = atEnd ? coords[n - 1]! : coords[0]!;
  for (let k = 1; k < n; k++) {
    const inner = atEnd ? coords[n - 1 - k]! : coords[k]!;
    if (!samePoint(inner, tip)) return bearingDeg(inner, tip);
  }
  return null;
}

/** Tracé dont les deux extrémités sont prolongées dans l'axe de leur segment terminal (`startM` au début,
 *  `endM` à la fin, en mètres, valeurs négatives ignorées) ; même nombre de points (interpolations intactes). */
export function extendEnds(coords: readonly LonLat[], startM: number, endM: number): LonLat[] {
  const out = coords.map((p) => [p[0], p[1]] as LonLat);
  const n = out.length;
  if (n < 2) return out;
  if (startM > 0) {
    const bearing = endBearing(coords, false);
    if (bearing !== null) out[0] = moveM(out[0]!, bearing, startM);
  }
  if (endM > 0) {
    const bearing = endBearing(coords, true);
    if (bearing !== null) out[n - 1] = moveM(out[n - 1]!, bearing, endM);
  }
  return out;
}
